import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppSidebar } from '../layout/AppSidebar';
import { MobileNav } from '../layout/MobileNav';
import { DefaultScreen } from './DefaultScreen';
import { SuggestionWizard } from '../suggestionWizard';
import { useSuggestionWizard } from '../../contexts/SuggestionWizardContext';
import { IdealCustomerProfile } from '../../types';
import { MonitorButtonRef } from '../monitoring/MonitorButton';
import { useAppData } from '../../contexts/AppDataContext';
import { OUT_OF_CREDITS_ERROR_MESSAGE } from '../../constants/error-messages';
import { useToast } from '../../hooks/useToast';
import { apiClient } from '../../lib/api';
import { exportTableToCSV, exportTableToExcel } from '../../utils/csvExport';
import { formatDateForFilename } from '../../utils/dateUtils';
import { transition } from '../../styles/tokens';
import {
  useTableData,
  useDashboardModals,
  useChatWorkflows,
  useRowSelection,
  useTablePersistentState,
  useSignalWizard,
} from '../../hooks/dashboard';
import { DatasetViewLayout } from './DatasetViewLayout';
import { DashboardModals } from './DashboardModals';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { DesktopDownloadBanner } from '../common/DesktopDownloadBanner';

interface LocationState {
  initialChatMessages?: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    criteria?: string[];
  }>;
}

export const Dashboard: React.FC = () => {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { removeDataset, refreshDatasets, updateDataset } = useAppData();
  const { isOpen: isWizardOpen, selectedIcp: wizardSelectedIcp, closeWizard } = useSuggestionWizard();
  const { showToast } = useToast();

  // Refs
  const professionalTableRef = useRef<any>(null);
  const monitorButtonRef = useRef<MonitorButtonRef>(null);

  // Local state for dataset creation
  const [isCreatingDataset, setIsCreatingDataset] = useState(false);
  const [leadGenerationError, setLeadGenerationError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ type: string; payload?: any } | null>(null);
  const [chatIcpContext] = useState<IdealCustomerProfile | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Custom hooks
  const tableData = useTableData({
    tableId,
    onDatasetUpdated: (id) => updateDataset(id, { updated_at: new Date().toISOString() }),
  });

  const modals = useDashboardModals();

  const chatWorkflows = useChatWorkflows({
    tableId,
    initialChatMessages: locationState?.initialChatMessages,
  });

  const rowSelection = useRowSelection();

  const persistentState = useTablePersistentState();

  const signalWizard = useSignalWizard({
    tableId,
    onNavigateAway: () => navigate('/datasets'),
    closeWizard,
  });

  // Extended chat action trigger to handle pending actions
  const handleChatActionTrigger = useCallback((action: { type: string; payload?: any }): boolean => {
    const handled = chatWorkflows.handleChatActionTrigger(action);
    if (!handled) {
      // For unhandled actions, set as pending
      setPendingAction(action);
      setTimeout(() => setPendingAction(null), transition.durationMs.fast);
    }
    return handled;
  }, [chatWorkflows]);

  // Listen for tour event to close chat panel on mobile
  useEffect(() => {
    const handleCloseChatPanel = () => {
      chatWorkflows.closeChatPanel();
    };

    window.addEventListener('tour-close-chat-panel', handleCloseChatPanel);
    return () => window.removeEventListener('tour-close-chat-panel', handleCloseChatPanel);
  }, [chatWorkflows]);

  // Handlers
  const handleCreateDataset = async (
    searchQuery: string | null,
    criteria: string[] | null,
    tableType: 'companies' | 'people' | 'custom',
    customType: string,
    rowCount: number,
    uploadId: string | null,
    title: string | null
  ) => {
    setIsCreatingDataset(true);
    setLeadGenerationError(null);

    try {
      const response = await apiClient.generateEnhancedLeadTable(
        searchQuery,
        criteria,
        tableType,
        rowCount,
        customType,
        uploadId,
        title
      );

      if (response?.table_id) {
        await refreshDatasets();
        setTimeout(() => {
          navigate(`/datasets/${response.table_id}`);
        }, transition.durationMs.fast);
      } else {
        setLeadGenerationError('Failed to create dataset - no table ID returned');
      }
    } catch (error: unknown) {
      console.error('Error creating dataset:', error);
      setLeadGenerationError(error instanceof Error ? error.message : 'Failed to create dataset');
      if (error instanceof Error && error.message === OUT_OF_CREDITS_ERROR_MESSAGE) {
        modals.openOutOfCreditsModal();
      }
    } finally {
      setIsCreatingDataset(false);
    }
  };

  const handleShare = useCallback(() => {
    if (tableId) {
      modals.openShareModal();
    }
  }, [tableId, modals]);

  const handleCreateWorkflow = useCallback(() => {
    modals.openWorkflowModal();
  }, [modals]);

  const handleExportCSV = useCallback(async () => {
    if (tableData.leads.length > 0) {
      const columnsToExport = professionalTableRef.current?.getColumns?.() || tableData.columns;
      if (columnsToExport.length > 0) {
        await exportTableToCSV(
          tableData.leads,
          columnsToExport,
          `${tableData.name.slice(0, 50)}-${formatDateForFilename()}.csv`,
          tableId
        );
      }
    }
  }, [tableData.leads, tableData.columns, tableData.name, tableId]);

  const handleExportExcel = useCallback(async () => {
    if (tableData.leads.length > 0) {
      const columnsToExport = professionalTableRef.current?.getColumns?.() || tableData.columns;
      if (columnsToExport.length > 0) {
        await exportTableToExcel(
          tableData.leads,
          columnsToExport,
          `${tableData.name.slice(0, 50)}-${formatDateForFilename()}.xlsx`,
          tableId
        );
      }
    }
  }, [tableData.leads, tableData.columns, tableData.name, tableId]);

  const handleDelete = useCallback(() => {
    if (!tableId) return;
    setShowDeleteModal(true);
  }, [tableId]);

  const handleConfirmDelete = useCallback(async () => {
    if (!tableId) return;

    setIsDeleting(true);
    try {
      await apiClient.deleteLeadTable(tableId);
      removeDataset(tableId);
      navigate('/datasets');
    } catch (error) {
      console.error('Error deleting table:', error);
      showToast('Failed to delete dataset. Please try again.', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  }, [tableId, removeDataset, navigate, showToast]);

  return (
    <div className="h-screen-safe bg-white flex overflow-hidden relative">
      {/* Mobile Navigation */}
      <MobileNav selectedDatasetId={tableId} />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden pt-14 md:pt-0">
        {/* App Sidebar */}
        <AppSidebar selectedDatasetId={tableId} />

        {/* Main Content Area */}
        {tableId ? (
          <DatasetViewLayout
            tableId={tableId}
            tableData={tableData}
            chatWorkflows={{
              ...chatWorkflows,
              handleChatActionTrigger,
            }}
            rowSelection={rowSelection}
            modals={modals}
            tableStates={persistentState.tableStates}
            onTableStateChange={persistentState.handleStateChange}
            chatIcpContext={chatIcpContext}
            pendingAction={pendingAction}
            professionalTableRef={professionalTableRef}
            monitorButtonRef={monitorButtonRef}
            onShare={handleShare}
            onExportCSV={handleExportCSV}
            onExportExcel={handleExportExcel}
            onDelete={handleDelete}
            onCreateWorkflow={handleCreateWorkflow}
            handleCreateDataset={handleCreateDataset}
            initialMessages={locationState?.initialChatMessages}
          />
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <DefaultScreen
              onCreateDataset={handleCreateDataset}
              leadGenerationError={leadGenerationError}
              isLoading={isCreatingDataset}
              initialSearchQuery={signalWizard.signalSearchQuery}
              initialTableType={signalWizard.signalTableType}
              initialPreviewData={signalWizard.signalPreviewData}
              onSearchQueryConsumed={signalWizard.handleSignalQueryConsumed}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <DashboardModals
        tableId={tableId}
        tableName={tableData.name}
        showShareModal={modals.showShareModal}
        onCloseShareModal={modals.closeShareModal}
        showWorkflowModal={modals.showWorkflowModal}
        onCloseWorkflowModal={modals.closeWorkflowModal}
        showOutOfCreditsModal={modals.showOutOfCreditsModal}
        onCloseOutOfCreditsModal={modals.closeOutOfCreditsModal}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Dataset"
        message={`Are you sure you want to delete "${tableData.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Signal Generator Wizard */}
      <SuggestionWizard
        isOpen={isWizardOpen}
        onClose={closeWizard}
        onSelectSuggestion={signalWizard.handleSignalSelect}
        onSelectMultiple={signalWizard.handleMultiSignalSelect}
        existingICPs={signalWizard.icpProfiles}
        preSelectedIcp={wizardSelectedIcp}
        onICPCreated={signalWizard.handleICPCreated}
      />

      {/* Desktop Download Banner */}
      <DesktopDownloadBanner
        isDatasetView={!!tableId}
        isChatOpen={chatWorkflows.isChatPanelOpen}
      />
    </div>
  );
};
